import { motion } from 'framer-motion';
import {
  Activity,
  Target,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  BarChart3,
  Users,
  Calendar,
  RefreshCw,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import Breadcrumb from '../components/Breadcrumb';
import PageHero from '../components/PageHero';
import { getPageHeroImageUrl } from '../config/cloudflareAssets';

export default function ExecutionMonitoringPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const executionPillars = [
    {
      icon: Target,
      title: 'OKRs & KPIs',
      subtitle: 'Measure What Matters',
      description:
        'Use objectives and measures to guide reviews of the projects and tasks supporting your strategy.',
      link: '/execution-monitoring/okrs-kpis',
      components: [
        {
          name: 'Objectives',
          description: 'What you want to achieve (qualitative goals)',
          examples: [
            'Become the market leader in our segment',
            'Build a world-class engineering team',
            'Achieve operational excellence',
          ],
        },
        {
          name: 'Key Results',
          description: 'How you measure success (quantitative metrics)',
          examples: [
            'Increase market share from 15% to 25%',
            'Hire 20 senior engineers by Q4',
            'Reduce operational costs by 30%',
          ],
        },
        {
          name: 'Leading vs. Lagging Indicators',
          description: 'Track both predictive metrics and outcome metrics',
          examples: [
            'Leading: Pipeline growth, customer engagement',
            'Lagging: Revenue, market share, profit',
            'Both types give you a complete picture',
          ],
        },
      ],
    },
    {
      icon: Calendar,
      title: 'Strategic Cadence',
      subtitle: 'Regular Review Rhythm',
      description: 'Establish a consistent schedule for strategic reviews and course corrections.',
      link: '/execution-monitoring/strategic-cadence',
      components: [
        {
          name: 'Annual Planning',
          description: 'Set strategic direction and major initiatives for the year',
          examples: [
            "Review previous year's results",
            'Update 3-5 year strategic plan',
            'Define annual objectives and budgets',
          ],
        },
        {
          name: 'Quarterly Reviews',
          description: 'Assess progress and adjust tactics',
          examples: [
            'Review OKR progress',
            'Adjust resource allocation',
            'Update initiative priorities',
          ],
        },
        {
          name: 'Monthly Check-ins',
          description: 'Monitor key metrics and address blockers',
          examples: [
            'Track leading indicators',
            'Identify and remove obstacles',
            'Celebrate wins and learn from failures',
          ],
        },
        {
          name: 'Weekly Standups',
          description: 'Maintain momentum and alignment',
          examples: [
            'Review priority initiatives',
            'Coordinate cross-functional work',
            'Make tactical decisions quickly',
          ],
        },
      ],
    },
    {
      icon: RefreshCw,
      title: 'Continuous Improvement',
      subtitle: 'Learn and Adapt',
      description:
        'Build a culture of learning from both successes and failures to improve over time.',
      link: '/execution-monitoring/continuous-improvement',
      components: [
        {
          name: 'Performance Reviews',
          description: "Systematically evaluate what's working and what isn't",
          examples: [
            'Analyze variance between targets and actuals',
            'Identify root causes of underperformance',
            'Document successful tactics to replicate',
          ],
        },
        {
          name: 'Strategic Pivots',
          description: 'Know when to stay the course and when to change direction',
          examples: [
            'Market conditions change significantly',
            'New data invalidates assumptions',
            'Better opportunities emerge',
          ],
        },
        {
          name: 'Knowledge Capture',
          description: 'Document lessons learned to build institutional knowledge',
          examples: [
            'What worked and why',
            "What didn't work and why",
            'How to apply learnings going forward',
          ],
        },
      ],
    },
    {
      icon: Users,
      title: 'Accountability & Ownership',
      subtitle: 'Clear Responsibility',
      description: 'Assign clear ownership for every strategic initiative and metric.',
      link: '/execution-monitoring/accountability-ownership',
      components: [
        {
          name: 'Initiative Owners',
          description: 'Each strategic initiative has a single accountable leader',
          examples: [
            'Product launch: VP Product',
            'Market expansion: VP Sales',
            'Digital transformation: CTO',
          ],
        },
        {
          name: 'RACI Matrix',
          description: 'Define who is Responsible, Accountable, Consulted, and Informed',
          examples: [
            'Eliminates confusion about roles',
            'Ensures no gaps or overlaps',
            'Speeds up decision-making',
          ],
        },
        {
          name: 'Performance Management',
          description: 'Link individual goals to strategic objectives',
          examples: [
            'Cascade objectives through org levels',
            'Tie compensation to strategic results',
            'Provide regular feedback and coaching',
          ],
        },
      ],
    },
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'See the work in context',
      description: 'Review how your projects and tasks contribute to the objectives they support.',
    },
    {
      icon: AlertCircle,
      title: 'Find the next question',
      description:
        'Use progress reviews to identify stalled work and decide what support is needed.',
    },
    {
      icon: CheckCircle,
      title: 'Make the next action clear',
      description: 'Break strategic priorities into work your team can own and move forward.',
    },
    {
      icon: RefreshCw,
      title: 'Learn and adjust',
      description:
        'Review what is changing and update the plan without losing the company direction.',
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Execution & Monitoring' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Activity className="w-4 h-4 mr-2" />
            Step 5 of 5
          </span>
        }
        title="Make your strategy part of the work."
        subtitle="With Pro Activation, connect strategic priorities to projects, tasks and clear ownership. Review progress, learn from what happens and keep your team focused on the next useful action."
      >
        <Link
          to="/sign-up"
          className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition-opacity"
        >
          Connect my strategy to delivery
        </Link>
      </PageHero>

      {/* Execution Pillars */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">Give the plan a practical rhythm.</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform your strategy from a document into a system that drives daily decisions and
              actions
            </p>
          </motion.div>

          <div className="space-y-16 max-w-6xl mx-auto">
            {executionPillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 border border-green-200"
              >
                <div className="flex flex-col md:flex-row gap-8 mb-8">
                  <div className="flex-shrink-0">
                    <div className="bg-gradient-to-br from-green-600 to-blue-600 w-20 h-20 rounded-xl flex items-center justify-center shadow-sm">
                      <pillar.icon className="w-10 h-10 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold mb-2">{pillar.title}</h3>
                    <p className="text-green-600 font-semibold mb-4 text-lg">{pillar.subtitle}</p>
                    <p className="text-gray-700 text-lg">{pillar.description}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-4">
                  {pillar.components.map((component, idx) => (
                    <div
                      key={idx}
                      className="public-card bg-white p-6 rounded-xl border border-green-200"
                    >
                      <h4 className="font-bold text-gray-900 mb-2">{component.name}</h4>
                      <p className="text-gray-600 mb-3 text-sm">{component.description}</p>
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-700">Examples:</p>
                        {component.examples.map((example, i) => (
                          <div key={i} className="flex items-start">
                            <div className="bg-green-400 w-1 h-1 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                            <span className="text-xs text-gray-600">{example}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Learn More Link */}
                <Link
                  to={pillar.link}
                  className="inline-flex items-center text-green-600 hover:text-green-700 font-semibold transition-colors"
                >
                  Learn More About {pillar.title} <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">
                Keep the work connected to the reason for doing it.
              </h2>
              <p className="text-xl text-gray-600">
                Most strategies fail not because they're wrong, but because they're never properly
                executed.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="public-card text-center bg-white p-6 rounded-xl shadow-sm"
                >
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How StratPlan Helps */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="public-card text-gray-900 p-10 rounded-2xl bg-blue-50"
            >
              <h2 className="text-3xl font-bold mb-6">
                One place to connect priorities, people and progress.
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <BarChart3 className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Connected progress views</h3>
                    <p className="text-gray-600 text-sm">
                      Review strategic priorities alongside the projects and tasks that support
                      them.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Clear ownership</h3>
                    <p className="text-gray-600 text-sm">
                      Make responsibility visible so your team knows who can move the work forward.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Users className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Shared direction</h3>
                    <p className="text-gray-600 text-sm">
                      Give people the company context behind their objectives and responsibilities.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <RefreshCw className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">A useful review rhythm</h3>
                    <p className="text-gray-600 text-sm">
                      Use regular check-ins to discuss progress, learn from changes and choose the
                      next action.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-4">
                A connected plan is the start of the work.
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                You've reached the final step. Now it's time to put everything together and execute.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/strategy-formulation"
                  className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  ← Previous: Strategy Formulation
                </Link>
                <Link
                  to="/foundation"
                  className="inline-flex items-center justify-center bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                >
                  Back to Step 1: Foundation
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="public-section public-cta py-16 bg-blue-50">
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={false} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Give every priority a way forward.
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Bring your direction, priorities and people into one plan, then connect each priority
              to the work that will move it forward.
            </p>
            <Link
              to="/sign-up"
              className="inline-flex items-center bg-white text-green-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Build my strategic plan <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
}
